import L from 'leaflet';
import { normalizePolylineForDateline } from '../utils/math.js';
import { getPlannedLatLngs } from '../utils/plannedRoute.js';
import { estimateShipSchedule } from '../utils/schedule.js';
import { getActiveRouteStyle, getPlannedRouteStyle } from './routeStyle.js';

const isDesktop = () => typeof window === 'undefined'
  || (window.innerWidth > 768 && !('ontouchstart' in window));

/**
 * Draws active shipping routes as polylines on the 'routes' layer.
 */
export class RouteRenderer {
  /**
   * @param {MapLayers} mapLayers
   * @param {{ getStyleContext?: () => { now?:Date, contracts?:any, tradeRoutes?:any } }} [opts]
   *   getStyleContext supplies the live game context for schedule colouring.
   */
  constructor(mapLayers, opts = {}) {
    this.mapLayers = mapLayers;
    this.getStyleContext = opts.getStyleContext || (() => ({}));
    this.lines     = new Map(); // shipId -> L.polyline (active voyage, solid)
    this.plannedLines = new Map(); // shipId -> L.polyline (held departure, dashed)
  }

  /** Schedule result for the ship's voyage, or null when it can't be judged */
  _getSchedule(ship) {
    try {
      const ctx = this.getStyleContext() || {};
      if (!(ctx.now instanceof Date)) return null;
      return estimateShipSchedule(ship, ctx);
    } catch (err) {
      return null;
    }
  }

  /** Route style (colour + cause) for an active line: worse of fuel and schedule */
  _getActiveStyle(ship) {
    return getActiveRouteStyle(ship, this._getSchedule(ship));
  }

  /** Route color for an active line (fuel thresholds unchanged, schedule can raise severity) */
  _getRouteColor(ship) {
    return this._getActiveStyle(ship).color;
  }

  /** Desktop sticky tooltip naming why the line is not green */
  _syncTooltip(line, cause) {
    if (!line || line._routeCause === cause) return;
    line._routeCause = cause;
    if (!cause || !isDesktop()) {
      line.unbindTooltip();
      return;
    }
    if (line.getTooltip && line.getTooltip()) line.setTooltipContent(cause);
    else line.bindTooltip(cause, { sticky: true, direction: 'top', className: 'route-status-tooltip' });
  }

  /** Re-apply colour/tooltip for one ship without touching geometry */
  restyle(ship) {
    if (!ship) return;
    const line = this.lines.get(ship.id);
    if (line) {
      const style = this._getActiveStyle(ship);
      line.setStyle({ color: style.color });
      this._syncTooltip(line, style.cause);
    }
    const planned = this.plannedLines.get(ship.id);
    if (planned) planned.setStyle({ color: getPlannedRouteStyle(this._getSchedule(ship)).color });
  }

  restyleAll(ships) {
    for (const ship of ships || []) this.restyle(ship);
  }

  /** Draw / update the route line for a ship */
  upsert(ship) {
    if (!ship.currentRoute) {
      this.remove(ship.id);
      this._upsertPlanned(ship);
      return;
    }
    this._removePlanned(ship.id);

    // Normalize for dateline crossing
    const normalized = normalizePolylineForDateline(ship.currentRoute.waypoints);
    const latlngs = normalized.map(w => [w.lat, w.lng]);
    const style   = this._getActiveStyle(ship);
    const color   = style.color;

    if (!this.lines.has(ship.id)) {
      const line = L.polyline(latlngs, {
        color,
        weight: 3,
        opacity: 0.8,
        dashArray: null // solid = active
      }).addTo(this.mapLayers.getLayer('routes'));
      this.lines.set(ship.id, line);
      this._syncTooltip(line, style.cause);
    } else {
      const line = this.lines.get(ship.id);
      line.setLatLngs(latlngs);
      line.setStyle({ color });
      this._syncTooltip(line, style.cause);
    }
  }

  /** Remove a route line */
  remove(shipId) {
    const line = this.lines.get(shipId);
    if (line) {
      line.remove();
      this.lines.delete(shipId);
    }
  }

  /** Dashed planned-route line for a ship whose departure is held in port */
  _upsertPlanned(ship) {
    const latlngs = getPlannedLatLngs(ship);
    if (!latlngs) {
      this._removePlanned(ship.id);
      return;
    }
    const color = getPlannedRouteStyle(this._getSchedule(ship)).color;
    const line = this.plannedLines.get(ship.id);
    if (line) {
      line.setLatLngs(latlngs);
      line.setStyle({ color });
    } else {
      this.plannedLines.set(ship.id, L.polyline(latlngs, {
        color,
        weight: 2,
        opacity: 0.7,
        dashArray: '6 8',
        interactive: false
      }).addTo(this.mapLayers.getLayer('routes')));
    }
  }

  _removePlanned(shipId) {
    const line = this.plannedLines.get(shipId);
    if (line) {
      line.remove();
      this.plannedLines.delete(shipId);
    }
  }

  /** Drop planned lines for ships that no longer exist */
  prunePlanned(shipExists) {
    for (const id of [...this.plannedLines.keys()]) {
      if (!shipExists(id)) this._removePlanned(id);
    }
  }
}
